<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { faces, combos, DieFace } from '../models/face.type';
  import Icon from '../icon/icon.svelte';
  import DieIcon from '../dice-tray/die-icon.svelte';
  import DieTicks from '../dice-tray/die-ticks.svelte';
  import { Die } from '../models/die.type';

  interface Props {
    die: Die;
    /** The tip is showing. */
    open?: boolean;
    /** This die has hints to give at all. */
    showInfo?: boolean;
    /** Those hints are unfolded, rather than still collapsed. */
    hintsOpen?: boolean;
    allDice?: Array<Die>;
    /**
     * Prefer sitting above the die. A finger covers whatever it is holding, so a
     * peek opened by a hold has to go where the hand is not.
     */
    above?: boolean;
    /** The die itself, to measure the tip against. */
    anchor?: HTMLElement;
  }

  let {
    die,
    open = false,
    showInfo = false,
    hintsOpen = false,
    allDice = [],
    above = false,
    anchor = undefined,
  }: Props = $props();

  let hoverContainer: HTMLElement | undefined = $state();
  let hintsInner: HTMLElement | undefined = $state();
  let marginOffset: string = $state('0');
  let placeAbove = $state(false);

  /** Clearance between the tip and the die. Matches @tip-gap below. */
  const GAP = 8;

  let title = $derived(die.type + ' ' + (die.level == 0 ? '' : die.level));
  let hintStrings: Array<Array<string>> = $state([]);

  // Both measure and then write what they measured, so each runs on its own
  // named triggers and nothing it reads inside can set it off again.
  $effect.pre(() => {
    void open;
    untrack(calculateOffset);
  });
  $effect.pre(() => {
    void [open, above, hintsOpen, anchor, hintsInner];
    untrack(placeTip);
  });

  /**
   * Above only if the whole tip fits there, so a die near the top of the screen
   * falls back to below rather than being cut off. The hints are measured even
   * while they are still collapsed, so the answer does not change under the
   * player a second after they unfold.
   */
  function placeTip() {
    if (!open || !above || !hoverContainer || !anchor) {
      placeAbove = false;
      return;
    }

    const tip = hoverContainer.getBoundingClientRect();
    const dieRect = anchor.getBoundingClientRect();
    const folded = hintsInner && !hintsOpen ? hintsInner.scrollHeight : 0;

    placeAbove = dieRect.top - (tip.height + folded + GAP) >= 0;
  }

  function calculateOffset() {
    // if hover container's calculated left position is less than 0, set it to 0
    let left = 0;
    let right = 0;

    if (hoverContainer) {
      const rect = hoverContainer.getBoundingClientRect();
      const offset = parseInt(marginOffset);
      if (rect.left - offset < 0) {
        left = rect.x - 5 - offset;
      } else if (rect.right - offset > window.innerWidth) {
        right = rect.x + rect.width + 5 - offset;
      }

      if (left < 0) {
        marginOffset = -left + 'px';
      } else if (right > window.innerWidth) {
        marginOffset = window.innerWidth - right + 'px';
      }
    }

    if (!marginOffset) {
      marginOffset = 'inherit';
    }
  }

  // in svelte style, when resize, calculate offset
  onMount(() => {
    // window.addEventListener('resize', calculateOffset);

    // get unique faces from die.faces
    const uniqueFaces: Array<DieFace> = Array.from(new Set(die.faces.flat()));

    // build string from unique faces, referencing the text field in faces
    hintStrings = [];

    uniqueFaces.forEach((face) => {
      const faceDef = faces.find((f) => f.id === face);
      if (faceDef) {
        let arr = faceDef.text.split(/\<|\>/g);

        if (arr.length > 0) {
          hintStrings.push(arr);
        }
      }
    });

    // also get combos that match the die type from the allDice array
    let comboFaces: Array<DieFace> = Array.from(
      new Set(
        allDice
          .map((d) => d.faces)
          .flat()
          .flat()
          // remove faces that are in uniqueFaces
          .filter((face) => !uniqueFaces.includes(face)),
      ),
    );

    let comboHints: Array<string> = [];
    uniqueFaces.forEach((face: DieFace) => {
      let combo = combos.filter((c) => c.icons.includes(face));

      // find a combo that also has a face from the comboface array
      combo = combo.filter((c) => {
        return c.icons.some((icon) => comboFaces.includes(icon));
      });

      combo.forEach((c) => {
        // concatenate to comboHints
        comboHints.push(c.text);
      });
      // remove duplicates
      comboHints = comboHints.filter((item, pos) => {
        return comboHints.indexOf(item) == pos;
      });
    });
    if (comboHints.length) {
      hintStrings.push(['- Combo -']);
      // choose random combo from combo array
      let index = Math.floor(Math.random() * comboHints.length);
      let arr = comboHints[index].split(/\<|\>/g);
      if (arr.length > 0) {
        hintStrings.push(arr);
      }
    }

    return () => {
      // window.removeEventListener('resize', calculateOffset);
    };
  });
</script>

<div class="hover-container {open ? 'hovering' : ''} {placeAbove ? 'above' : ''}">
  <div class="hover" bind:this={hoverContainer} style="margin-left: {marginOffset}">
    <h1>{title}</h1>

    <div class="die-hover-container">
      <div class="die {die.colour} {die.activeFace == 0 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[0]} sizeMod={1} />
        </div>
      </div>
      <div class="die {die.colour} {die.activeFace == 1 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[1]} sizeMod={1} />
        </div>
      </div>
      <div class="die {die.colour} {die.activeFace == 2 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[2]} sizeMod={1} />
        </div>
      </div>
      <div class="die {die.colour} {die.activeFace == 3 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[3]} sizeMod={1} />
        </div>
      </div>
      <div class="die {die.colour} {die.activeFace == 4 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[4]} sizeMod={1} />
        </div>
      </div>
      <div class="die {die.colour} {die.activeFace == 5 ? 'selected' : 'unselected'}">
        <div class="die-2d face">
          <DieTicks level={die.level}></DieTicks>
          <DieIcon face={die.faces[5]} sizeMod={1} />
        </div>
      </div>
    </div>

    {#if showInfo}
      <div class="hints {hintsOpen ? 'open' : ''}">
        <div class="hints-inner" bind:this={hintsInner}>
          {#each hintStrings as hint}
            <p>
              {#each hint as item}
                {#if item.startsWith(':')}
                  <Icon name={item.replace(/:/g, '')} class="icon" />
                {:else}
                  {item}
                {/if}
              {/each}
            </p>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="less">
  @import '../../defines.less';

  // Clearance between the tip and the die it belongs to. Matches GAP above,
  // which measures whether the tip fits above before it is put there.
  @tip-gap: 8px;
  .die-2d {
    min-width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 2px;
  }

  .hover-container {
    position: absolute;
    z-index: 111;
    // height: 0;
    opacity: 0;
    // No delay: the wait before a tip opens is a dwell, and die.svelte times it
    // so that a pointer arriving and a finger holding still measure the same
    // one. Fading out stays immediate either way.
    transition: opacity 0.3s;
    pointer-events: none;
    margin-left: calc(var(--dice-size) / 2 - (335px / 2));

    // A finger covers the die it is holding, so a peek sits above it instead.
    // Pinned by its bottom edge, so unfolding the hints grows it upwards, away
    // from the hand rather than back under it.
    &.above {
      bottom: calc(100% + @tip-gap);
    }

    --icon-size: 20px;

    p {
      // display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-bottom: 5px;
      grid-gap: 3px;
    }

    @media @large {
      margin-left: calc(var(--dice-size) / 2 - (310px / 2));
    }
    @media @medium {
      margin-left: calc(var(--dice-size) / 2 - (280px / 2));
    }
    @media @small {
      margin-left: calc(var(--dice-size) / 2 - (250px / 2));
    }

    .hover {
      position: relative;
      // height: 0;
      overflow: hidden;
      z-index: 11;
      background-color: var(--background-colour);
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      transition:
        width 0.3s,
        height 0.3s,
        padding 0.3s;
      outline: 1px solid black;

      width: 325px;
      padding: 5px;

      @media @large {
        width: 300px;
      }
      @media @medium {
        width: 270px;
      }
      @media @small {
        width: 240px;
      }

      h1 {
        font-size: 20px;
        color: white;
        text-transform: capitalize;
        margin-bottom: 5px;
      }

      .die-hover-container {
        display: flex;
        grid-gap: var(--dice-gap);

        .die {
          width: calc(var(--dice-size) / 2 + 10px);
          height: calc(var(--dice-size) / 2 + 10px);
          position: relative;
        }
      }

      // The hints arrive a second after the tip does, so they unfold rather
      // than appear: a single-row grid animated from 0fr to 1fr, which is the
      // only way to transition to a height nobody has measured.
      .hints {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease;

        &.open {
          grid-template-rows: 1fr;
        }

        .hints-inner {
          overflow: hidden;
          min-height: 0;
          padding-top: 5px;
        }
      }
    }

    &.hovering {
      display: flex;
      opacity: 1;

      .hover {
        // height: 77px;
        padding: 5px;

        @media @medium {
          // height: 70px;
        }
        @media @small {
          // height: 65px;
        }
      }
    }
  }
</style>
